import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Utils from "../../../util/utils";
import {
  BODY_TYPES,
  EMPTY_FILTERS,
  VEHICLE_TYPES,
} from "../mockData";

function extractCityUf(value) {
  const match = String(value || "").match(/^(.*?)[,-/]\s*([A-Za-z]{2})$/);
  if (!match) return null;
  return {
    city: match[1].trim(),
    uf: match[2].trim().toUpperCase(),
  };
}

function buildCityOptions(input) {
  const raw = String(input || "").trim();
  if (!raw) return [];

  const parsed = extractCityUf(raw);
  const result =
    parsed?.city && parsed?.uf
      ? Utils.buscarMunicipioPorNomeEUF(parsed.city, parsed.uf)
      : Utils.buscarMunicipiosPorNome(raw);

  const uniqueNames = Array.from(
    new Set(
      (result || [])
        .map((item) => {
          const city = item?.xMunCarrega;
          const uf = item?.UF;
          if (!city) return null;
          return uf ? `${city} - ${uf}` : city;
        })
        .filter(Boolean),
    ),
  );

  return uniqueNames.slice(0, 30);
}

export default function FreightFilters({
  filters,
  onChange,
  onSearch,
  onClear,
  loading = false,
}) {
  const [originOptions, setOriginOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);

  const handleChange = (field) => (event) => {
    onChange({ ...filters, [field]: event.target.value });
  };

  const handleCityChange = (field) => (_, value) => {
    const next =
      typeof value === "string" ? value : value == null ? "" : String(value);
    onChange({ ...filters, [field]: next });
  };

  const handleOnlyMyShippersChange = (event) => {
    onChange({ ...filters, onlyMyShippers: event.target.checked });
  };

  const handleClear = () => {
    onChange({ ...EMPTY_FILTERS });
    setOriginOptions([]);
    setDestinationOptions([]);
    onClear();
  };

  const originValue = useMemo(
    () => filters.origin || "",
    [filters.origin],
  );
  const destinationValue = useMemo(
    () => filters.destination || "",
    [filters.destination],
  );

  return (
    <Card className="lances-filters-card" elevation={0}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Nº do Frete"
              placeholder="Ex: 1779894457614"
              value={filters.number}
              onChange={handleChange("number")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              freeSolo
              size="small"
              options={originOptions}
              value={originValue}
              inputValue={originValue}
              onInputChange={(_, input, reason) => {
                if (reason === "input" || reason === "clear") {
                  setOriginOptions(buildCityOptions(input));
                  if (reason === "clear") {
                    handleCityChange("origin")(_, "");
                    return;
                  }
                  onChange({ ...filters, origin: input });
                }
              }}
              onChange={handleCityChange("origin")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Origem"
                  placeholder="Ex: Belo Horizonte - MG"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              freeSolo
              size="small"
              options={destinationOptions}
              value={destinationValue}
              inputValue={destinationValue}
              onInputChange={(_, input, reason) => {
                if (reason === "input" || reason === "clear") {
                  setDestinationOptions(buildCityOptions(input));
                  if (reason === "clear") {
                    handleCityChange("destination")(_, "");
                    return;
                  }
                  onChange({ ...filters, destination: input });
                }
              }}
              onChange={handleCityChange("destination")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destino"
                  placeholder="Ex: São Paulo - SP"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Veículo</InputLabel>
              <Select
                label="Tipo de Veículo"
                value={filters.vehicleType}
                onChange={handleChange("vehicleType")}
              >
                <MenuItem value="">Todos</MenuItem>
                {VEHICLE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Carroceria</InputLabel>
              <Select
                label="Tipo de Carroceria"
                value={filters.bodyType}
                onChange={handleChange("bodyType")}
              >
                <MenuItem value="">Todos</MenuItem>
                {BODY_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Data da Coleta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.collectionDate}
              onChange={handleChange("collectionDate")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              className="lances-filters-my-shippers"
              control={
                <Checkbox
                  checked={filters.onlyMyShippers !== false}
                  onChange={handleOnlyMyShippersChange}
                  size="small"
                />
              }
              label="Meus embarcadores"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box className="lances-filters-actions">
              <Button
                variant="text"
                onClick={handleClear}
                className="lances-btn-clear"
                disabled={loading}
              >
                Limpar filtros
              </Button>
              <Button
                variant="contained"
                onClick={onSearch}
                className="lances-btn-search"
                disabled={loading}
              >
                Buscar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
